import {
  CardContainer,
  isImageOr3dModel,
  isPdfOrFile,
  MenuButton,
  processAttachment,
} from "@msys/ui";
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React, { useRef, useState } from "react";
import { ItemAttachment } from "../../../clients/graphqlTypes.js";
import { GalleryGrid } from "../../commons/images/GalleryGrid.js";
import { ViewMode, ViewModeMenuItem } from "../../commons/ViewModeMenuItem.js";
import { AttachmentUploader } from "./AttachmentUploader.js";
import { FileBoxRow } from "./FileBoxRow.js";
import { Attachment, getRotatedUrl } from "./helpers.js";
import {
  AddAttachmentsFn,
  ModifyAttachmentFn,
  RemoveAttachmentFn,
} from "./useAttachments.js";

interface Props {
  title?: string;
  attachments: Array<Attachment | ItemAttachment> | undefined;
  gridColumns?: number;
  canSign?: boolean;
  canEdit?: boolean;
  defaultPlace?: string;
  defaultDate?: any;
  isInitiallyClosed?: boolean;
  noItemsText?: string;
  setClientVisibility?: (attachmentId: string, isVisible: boolean) => void;
  renderImages?: boolean;
  addAttachments?: AddAttachmentsFn;
  modifyAttachment?: ModifyAttachmentFn;
  removeAttachment?: RemoveAttachmentFn;
  loading?: boolean;
  renderAddIconButton?: (button: React.ReactNode) => React.ReactNode;
  renderEditIconButton?: (button: React.ReactNode) => React.ReactNode;
}

const defaultRenderButton = (button: React.ReactNode) => button;

/** @deprecated Use FilesBox in commons */
export function FilesBoxTable({
  title,
  gridColumns = 3,
  canSign = false,
  canEdit = true,
  defaultPlace,
  defaultDate,
  isInitiallyClosed,
  noItemsText,
  attachments = [],
  setClientVisibility,
  renderImages = true,
  addAttachments,
  modifyAttachment,
  removeAttachment,
  loading,
  renderAddIconButton = defaultRenderButton,
  renderEditIconButton = defaultRenderButton,
}: Props) {
  const { t } = useTranslate("FilesBoxTable");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pictureInputRef = useRef<HTMLInputElement>(null);

  const [isDeletable, setIsDeletable] = useState(false);

  const typedAttachments = attachments.map(processAttachment);
  const images = renderImages ? typedAttachments.filter(isImageOr3dModel) : [];
  const files = renderImages
    ? typedAttachments.filter(isPdfOrFile)
    : typedAttachments;

  const handleAdd =
    canEdit && addAttachments
      ? async () => {
          if (pictureInputRef.current) {
            pictureInputRef.current.click();
          }
        }
      : undefined;

  const handleRotate =
    canEdit && modifyAttachment
      ? async (attachment: Attachment, direction: "right" | "left") => {
          await modifyAttachment(
            attachment.id,
            getRotatedUrl(attachment.url, direction)
          );
        }
      : undefined;

  const handleDelete =
    canEdit && removeAttachment
      ? async (attachment: Attachment) => {
          await removeAttachment(attachment.id);
        }
      : undefined;

  const canChangeVisibility = Boolean(setClientVisibility);
  const viewModes: ViewMode[] = [
    null,
    ...(canChangeVisibility ? ["visibility" as const] : []),
    ...(canEdit && attachments.length > 0 ? ["delete" as const] : []),
  ];
  const [viewMode, setViewMode] = React.useState<ViewMode>(
    viewModes[0] ?? null
  );

  return (
    <CardContainer
      Icon={<AttachFileIcon />}
      title={title ?? t("Files")}
      itemCount={attachments.length}
      isInitiallyClosed={isInitiallyClosed}
      ActionButton={
        <Stack
          direction={"row"}
          spacing={1 / 2}
          sx={{
            alignItems: "center",
          }}
        >
          {canEdit &&
            renderAddIconButton(
              <Tooltip title={t("Upload file")}>
                <IconButton
                  aria-label={t("Upload file")}
                  color="primary"
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          {viewModes.length > 1 &&
            renderEditIconButton(
              <MenuButton>
                <ViewModeMenuItem
                  allowedModes={viewModes}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />
              </MenuButton>
            )}
        </Stack>
      }
    >
      {loading && <LinearProgress />}
      <Stack direction="column" spacing={0}>
        {images.length > 0 && (
          <Box
            sx={{
              padding: 1,
            }}
          >
            <GalleryGrid
              images={images}
              columns={gridColumns}
              handleAdd={handleAdd}
              handleRotate={handleRotate}
              handleDelete={handleDelete}
              showDeleteButton={canEdit && isDeletable}
            />
          </Box>
        )}
        {files.length > 0 && (
          <List disablePadding>
            {files.map((file, index, array) => (
              <FileBoxRow
                key={file.id}
                file={file}
                onDeleteFile={
                  canEdit && removeAttachment
                    ? async attachmentId => {
                        await removeAttachment(attachmentId);
                      }
                    : undefined
                }
                // divider={index < array.length - 1}
                defaultPlace={defaultPlace}
                defaultDate={defaultDate}
                canSign={canSign}
                onSignedPdfAttachment={
                  canSign && addAttachments
                    ? async attachment => {
                        await addAttachments([attachment]);
                      }
                    : undefined
                }
                viewMode={viewMode}
                setClientVisibility={canEdit ? setClientVisibility : undefined}
                renderSignIconButton={renderAddIconButton}
                renderDeleteIconButton={renderEditIconButton}
                renderChangeVisibilityIconButton={renderEditIconButton}
              />
            ))}
          </List>
        )}
      </Stack>
      <AttachmentUploader
        innerRef={fileInputRef}
        accept="*"
        multiple={true}
        onComplete={async attachments => {
          await addAttachments?.(attachments);
        }}
      />
      <AttachmentUploader
        innerRef={pictureInputRef}
        accept="image/*,.heic,.heif"
        multiple={true}
        onComplete={async attachments => {
          await addAttachments?.(attachments);
        }}
      />
      {attachments.length === 0 && (
        <Stack
          direction={"column"}
          spacing={1}
          sx={{
            alignItems: "center",
            padding: 1,
          }}
        >
          {noItemsText && (
            <Typography variant="caption" color="gray" align="center">
              {noItemsText}
            </Typography>
          )}
          {canEdit &&
            renderAddIconButton(
              <Button
                startIcon={<AddIcon />}
                color="secondary"
                variant="text"
                onClick={() => {
                  fileInputRef.current?.click();
                }}
              >
                {t("Upload file")}
              </Button>
            )}
        </Stack>
      )}
    </CardContainer>
  );
}
